import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck,
  FileCheck2,
  MapPin,
  Settings2,
  ArrowRight,
  Play,
  MessageCircle,
} from "lucide-react";

// Modern luxury sedan, cinematic dark — Mercedes / luxury class
const HERO_IMG =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=2400&q=85";

const WHATSAPP_NUMBER = "34633821110";
const WHATSAPP_MSG = encodeURIComponent(
  "Hola Elecars, me gustaría hablar ahora con un asesor sobre la importación de un vehículo desde Alemania."
);

const trust = [
  { icon: ShieldCheck, label: "Revisión 200 puntos" },
  { icon: FileCheck2, label: "Proceso transparente" },
  { icon: MapPin, label: "Importación DE → ES" },
  { icon: Settings2, label: "Gestión completa" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.95]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end pb-16 sm:pb-20 pt-32"
      data-testid="hero-section"
    >
      {/* Cinematic background */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Vehículo premium alemán"
          className="w-full h-full object-cover object-center ken-burns"
        />
      </motion.div>

      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#0A0A0A]"
      />
      {/* Stronger left-side overlay so headline always reads clean over the vehicle */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-transparent" />
      <div className="hidden lg:block absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-black/40 to-transparent" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, delay: 0.4 }}
        className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[700px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(212,175,55,0.14) 0%, transparent 65%)",
        }}
      />

      {/* Clean unified bottom transition into the next section */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full"
      >
        <div className="max-w-5xl">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 mb-7"
          >
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: 48 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="h-px bg-[#D4AF37]"
            />
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-medium">
              Servicio premium · Importación profesional
            </span>
          </motion.div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="font-heading font-extralight tracking-tight text-[2rem] leading-[1.08] sm:text-5xl sm:leading-[1.06] lg:text-[5.25rem] lg:leading-[1.04] text-[#FFFFF0] max-w-[18ch] sm:max-w-[19ch]"
          >
            <HeadlineReveal>
              {["Importamos", "vehículos", "premium", "desde"]}
            </HeadlineReveal>{" "}
            <GoldWord delay={0.55}>Alemania</GoldWord>{" "}
            <HeadlineReveal delay={0.75}>
              {[
                "con",
                "revisión",
                "previa,",
                "total",
                "transparencia",
                "y",
                "acompañamiento",
                "profesional.",
              ]}
            </HeadlineReveal>
          </h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4 }}
            className="mt-7 sm:mt-9 max-w-2xl text-sm sm:text-base lg:text-lg leading-relaxed text-[#B5B5B0] font-light"
          >
            En <span className="text-[#FFFFF0]">Elecars</span> te ayudamos a
            buscar, revisar, negociar e importar coches, SUVs y furgonetas desde
            Alemania hasta España, con un proceso claro, seguro y un equipo
            comprometido en darte el mejor servicio.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.6 }}
            className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <a
              href="#contacto"
              data-testid="hero-primary-cta"
              className="group relative inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-6 sm:px-8 py-3.5 sm:py-4 text-xs sm:text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#E5C158] transition-all duration-300 overflow-hidden cta-pulse"
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
              <span className="relative">Solicitar asesoramiento</span>
              <ArrowRight
                className="relative w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300"
                strokeWidth={1.5}
              />
            </a>

            <a
              href="#proceso"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center justify-center gap-3 border border-white/20 text-[#FFFFF0] px-6 sm:px-8 py-3.5 sm:py-4 text-xs sm:text-sm uppercase tracking-[0.2em] font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              <Play className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              Ver cómo funciona
            </a>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-tertiary-cta"
              className="group inline-flex items-center justify-center gap-3 text-[#A3A3A3] hover:text-[#FFFFF0] px-3 sm:px-4 py-3.5 sm:py-4 text-xs sm:text-sm uppercase tracking-[0.2em] font-medium transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              Hablar ahora con un asesor
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </a>
          </motion.div>

          {/* Trust badges */}
          <div className="mt-12 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl">
            {trust.map((t, i) => (
              <motion.div
                key={t.label}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.8 + i * 0.12 }}
                whileHover={{ y: -3 }}
                className="flex items-center gap-3 bg-white/[0.06] backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/40 px-3 sm:px-4 py-3 transition-colors"
                data-testid={`trust-badge-${i}`}
              >
                <t.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] sm:text-sm text-[#FFFFF0] font-light leading-tight">{t.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator — refined, static, premium */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.2 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 z-20"
      >
        <span className="text-[10px] uppercase tracking-[0.35em] text-[#A3A3A3]">
          Scroll
        </span>
        <motion.span
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
          style={{ boxShadow: "0 0 12px rgba(212,175,55,0.6)" }}
        />
      </motion.div>
    </section>
  );
};

const HeadlineReveal = ({ children, delay = 0 }) => (
  <>
    {children.map((word, i) => (
      <motion.span
        key={`${word}-${i}`}
        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{
          duration: 0.9,
          delay: delay + i * 0.06,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="inline-block mr-[0.2em] whitespace-nowrap"
      >
        {word}
      </motion.span>
    ))}
  </>
);

const GoldWord = ({ children, delay = 0 }) => (
  <motion.span
    initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    className="relative inline-block font-light gold-text mr-[0.25em] whitespace-nowrap px-[0.08em] tracking-[-0.005em]"
  >
    {children}
    <motion.span
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 1, delay: delay + 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="absolute left-[0.08em] right-[0.08em] -bottom-1 h-px bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/80 to-[#D4AF37]/0 origin-center"
    />
  </motion.span>
);

export default Hero;
